import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiInputsDeviceBehaviorPlugin } from '@shure/device-ui/inputs/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiOutputsDeviceBehaviorPlugin } from '@shure/device-ui/outputs/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind,
	SchematicsDeviceChannel
} from '@shure/device-ui/schematics/data-access';
import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType, DspChannelType, DspCopyChannelProperty } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { ChannelGroup, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class ImxRoomDeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		DeviceUiOutputsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceUiInputsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.IMXROOM];

	constructor(protected readonly transloco: TranslocoService) {}

	public getInputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DanteMicInput,
			ChannelGroup.DanteLineInput,
			ChannelGroup.VirtualAudioInput,
			ChannelGroup.PcInput
		];
	}

	public getOutputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteLineOutput, ChannelGroup.VirtualAudioOutput, ChannelGroup.PcOutput];
	}

	public getAutomixedChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteMicInput];
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return true;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.automixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(
				([
					schematicLabel,
					inputsLabel,
					automixerLabel,
					matrixMixerLabel,
					outputsLabel,
					presetsLabel,
					settingsLabel
				]) => [
					{
						route: DeviceUiRoute.Schematic,
						label: schematicLabel
					},
					{
						route: DeviceUiRoute.Inputs,
						label: inputsLabel
					},
					{
						route: DeviceUiRoute.Intellimix,
						label: automixerLabel
					},
					{
						route: DeviceUiRoute.Matrixmixer,
						label: matrixMixerLabel
					},
					{
						route: DeviceUiRoute.Outputs,
						label: outputsLabel
					},
					{
						route: DeviceUiRoute.Presets,
						label: presetsLabel
					},
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					}
				]
			)
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset-page.restore-to-default'),
			button: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset-page.restore-to-defaults')
		};
	}

	public showPatentInfo(_model: DeviceModel): boolean {
		return true;
	}

	public enableStereoAudio(_model: DeviceModel, numberOfChannels: number): boolean {
		return numberOfChannels === 8;
	}

	public getOutputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Compressor, 2],
			[DspType.Delay, 3]
		]);
	}

	public getInputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2],
			[DspType.Aec, 3],
			[DspType.Nr, 4],
			[DspType.Agc, 5]
		]);
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.NotSupported;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return true;
	}

	public hideDspButtons(_model: DeviceModel): boolean {
		return true;
	}

	public showChannelCount(_model: DeviceModel): boolean {
		return true;
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteMicInput, 0],
			[ChannelGroup.Automix, 1],
			[ChannelGroup.DanteLineInput, 2],
			[ChannelGroup.VirtualAudioInput, 3],
			[ChannelGroup.PcInput, 4]
		]);
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['equalizer', 1],
			['compressor', 2],
			['delay', 3]
		]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		_channelType: DspChannelType,
		_selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		return ['muted', 'gain', 'analogLevel', 'pcPeripheral', 'deviceSpecificEqualizerContour'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public displayAutomixerDirectOutTapPoint(_model: DeviceModel): boolean {
		return true;
	}

	public getDirectChannelGroups(_model: DeviceModel): undefined {
		return undefined;
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}

	public showExportLogButton(_model: DeviceModel): boolean {
		return false;
	}
}
